import { useEffect, useState } from "react";
import { useAuditLogger } from "@hooks/use-audit-logger";
import { Logger } from "@utils/logger";
import { submitCaptures } from "@requests/submit-captures";
import { useSelector } from "react-redux";
import { getQueryParametersState } from "@redux/selectors/query-parameters-selectors";
import { getCaptureSteps, getResponseToken } from "@redux/selectors/verification-step-config-selectors";
import { datadogRum } from "@datadog/browser-rum";
import { config } from "node-config-ts";
import { LoadingSpinner } from "../common/loading-spinner/loading-spinner";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { AuditLogTypes } from "@constants/audit-log-types";
import styles from "./complete-page.less";
import { getIsTmxProfilingCompleted, getTmxError } from "@redux/selectors/tmx-selectors";
import { getTmxInDCUI, getTMXSessionId, getVerificationConfiguration } from "@redux/selectors/verification-configuration-selectors";
import { SanitizedHtmlContainer } from "@iddataweb/idw-common-ui/containers";

export const CompletePage = () => {
    const { writeAuditLog } = useAuditLogger();
    const queryParameters = useSelector(getQueryParametersState);
    const verificationConfiguration = useSelector(getVerificationConfiguration);
    const responseToken = useSelector(getResponseToken);
    const captureSteps = useSelector(getCaptureSteps);
    const content = useSelector(getLanguageContent);
    const [hasStartedSubmitting, setHasStartedSubmitting] = useState(false);
    const [hasCompletedSubmitting, setHasCompletedSubmitting] = useState(false);
    const [submitCaptureError, setSubmitCaptureError] = useState(null);
    const [redirectUrl, setRedirectUrl] = useState(null);

    const isTmxProfilingComplete = useSelector(getIsTmxProfilingCompleted);
    const hasTmxError = useSelector(getTmxError);
    const isTmxEnabled = useSelector(getTmxInDCUI);
    const tmxSessionID = useSelector(getTMXSessionId);

    const adjustRedirectUrl = (redirectUrl: string) => {
        if (!redirectUrl) {
            return null;
        }

        let finalUrl = redirectUrl;
        Logger.info('[CaptureConfigurationService] Pre formatting redirect URL', redirectUrl);
        try {
            const adjustedUrl = new URL(redirectUrl);
            if (adjustedUrl.hostname.includes(window.location.hostname)) {
                const baseUrl = queryParameters.apiUrl ? queryParameters.apiUrl : config.API_URL;
                adjustedUrl.hostname = baseUrl.split('https://')[1].split('/')[0];
                adjustedUrl.port = '';
                finalUrl = adjustedUrl.toString();
            }
        } catch (error) {
            Logger.error('[CaptureConfigurationService] Exception when trying to parse and process the redirect URL: ', error);
            const err = new Error(
                '[CaptureConfigurationService] Exception when trying to parse and process the redirect URL'
            );
            datadogRum.addError(err, { error });
        }

        Logger.info('[CaptureConfigurationService] Final redirect URL', finalUrl);
        return finalUrl;
    };

    const performSubmission = async () => {
        setHasStartedSubmitting(true);
        writeAuditLog(AuditLogTypes.DCUI_SAVE_ATTEMPTED);
        Logger.info(`[Step] DCUI_SAVE_ATTEMPTED`);
        const requestStartTime = new Date().getTime();

        try {
            const response = await submitCaptures(
                queryParameters,
                responseToken,
                captureSteps,
                verificationConfiguration.asi
            );

            const requestEndTime = new Date().getTime() - requestStartTime;
            Logger.info(`[DocumentCaptureEffects] Request duration (s): ${requestEndTime / 1000}`);
            Logger.info(`[DocumentCaptureEffects] AXN Response: `, JSON.stringify(response || {}));
            writeAuditLog(AuditLogTypes.DCUI_ALL_STEPS_COMPLETE);
            const json = await response.json();
            if (!json) {
                Logger.warn(`[Step] DCUI_SAVE_COMPLETE_WITH_ERROR `);
                const dataDogErr = new Error(`[Step] DCUI_SAVE_COMPLETE_WITH_ERROR`);
                datadogRum.addError(dataDogErr, { error: "JSON response was null/undefined" });
            } else if (json.errorMessage && !json.redirectURL) {
                Logger.warn(`[Step] DCUI_SAVE_COMPLETE_WITH_ERROR`, json.errorMessage);
                const dataDogErr = new Error(`[Step] DCUI_SAVE_COMPLETE_WITH_ERROR`);
                datadogRum.addError(dataDogErr, { error: json.errorMessage });
            } else {
                Logger.info(`[Step] DCUI_SAVE_COMPLETE_WITH_SUCCESS`);
                if (json.redirectURL) {
                    const adjustedRedirectUrl = adjustRedirectUrl(json.redirectURL);
                    setRedirectUrl(adjustedRedirectUrl);
                }
                setHasCompletedSubmitting(true);
            }
        } catch (error) {
            writeAuditLog(AuditLogTypes.DCUI_SAVE_ERROR);
            Logger.error(`[Step] DCUI_SAVE_ERROR`, error);
            const dataDogErr = new Error(`[Step] DCUI_SAVE_ERROR`);
            datadogRum.addError(dataDogErr, { error });
            setSubmitCaptureError("An unknown error occured while saving your document.")
            setHasCompletedSubmitting(true);
        }
    }

    const tmxProfilingCompleted = () => {
        Logger.info('[TMX] tmxProfilingCompleted with sessionID', tmxSessionID);
        performSubmission();
        Logger.info('[TMX] tmxProfilingCompleted step done');
    };

    useEffect(() => {
        if (!hasStartedSubmitting) {
            if (!isTmxEnabled || (isTmxEnabled && hasTmxError)) {
                performSubmission();
            } else {
                if (isTmxProfilingComplete) {
                    tmxProfilingCompleted();
                }
            }
        }
    }, [hasStartedSubmitting, isTmxEnabled, isTmxProfilingComplete]);

    useEffect(() => {
        if (hasCompletedSubmitting && redirectUrl) {
            setTimeout(() => {
                window.location.href = redirectUrl;
            }, 400);
        }
    }, [hasCompletedSubmitting, redirectUrl]);

    const captureUploadInProgressTitle = content.captureComplete.captureUploadTitle;
    const captureUploadCompleteTitle = content.captureComplete.captureUploadCompleteTitle;
    const captureUploadErrorMessage = content.captureComplete.captureUploadErrorMessage;
    const captureSuccessNoRedirectMessage = content.captureComplete.captureSuccessNoRedirectMessage;
    const captureSuccessMessage = content.captureComplete.captureSuccessMessage;

    return (
        <div className={styles.completePage}>
            {(!hasStartedSubmitting || !hasCompletedSubmitting) && (
                <div className={styles.titleContainer}>
                    <h1>{captureUploadInProgressTitle}</h1>
                    <LoadingSpinner loadingText={null} />
                </div>
            )}
            {hasStartedSubmitting && hasCompletedSubmitting && (
                <>
                    <div className={styles.titleContainer}>
                        <h1>{captureUploadCompleteTitle}</h1>
                    </div>
                    <div>
                        {submitCaptureError && (
                            <>
                                <SanitizedHtmlContainer html={`<span>${captureUploadErrorMessage}</span>`} />{" "}
                                <span>{submitCaptureError}</span>
                            </>
                        )}
                        {!submitCaptureError && !redirectUrl && (
                            <SanitizedHtmlContainer html={`<span>${captureSuccessNoRedirectMessage}</span>`} />
                        )}

                        {!submitCaptureError && redirectUrl && (
                            <SanitizedHtmlContainer html={`<span>${captureSuccessMessage}</span>`} />
                        )}
                    </div>
                </>
            )}
        </div>
    )
};